import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CustomTypography } from "../../custom-typography/custom-typography";
import {
  addAssessment,
  deleteAssessment,
  getActualAssessmentList,
  type ActualAssessmentListItem,
} from "../../../axios/admin";
import { AdminCreds } from "../../../types";
import { getAssessmentApiErrorMessage } from "../upload-assessment-modal/assessment-api-error";
import {
  CONTEST_PRESETS,
  DEFAULT_ANSWERS_TIME_MINUTES,
  DEFAULT_PRESET_ID,
  type ContestPresetId,
} from "../upload-assessment-modal/contest-presets";
import {
  applyContestSettingsToJson,
  defaultContestStartTime,
  hydrateContestSettings,
  tryParseContestJson,
  type ContestFormSettings,
  type ContestSendEmailChoice,
} from "../upload-assessment-modal/contest-json";
import {
  CONTEST_TIMEZONES,
  DEFAULT_TIMEZONE_ID,
  convertWallTimeBetweenZones,
  type ContestTimezoneId,
} from "../upload-assessment-modal/contest-timezone";
import {
  buttonsContainerStyles,
  contestItemStyles,
  contestMetaTypographyStyles,
  contestTitleTypographyStyles,
  dayStyles,
  emptyListTypographyStyles,
  errorTypographyStyles,
  fieldContainerStyles,
  fieldsGridStyles,
  getInputLabelStyles,
  getSelectStyles,
  getTextFieldStyles,
  headerTypographyStyles,
  hintTypographyStyles,
  jsonTextFieldStyles,
  layoutStyles,
  listContainerStyles,
  listLoadingStyles,
  mainButtonStyles,
  menuItemStyles,
  modalBoxStyles,
  popperStyles,
  presetMenuPaperStyles,
  secondaryButtonStyles,
  selectedHintStyles,
  successTypographyStyles,
  yearButtonStyles,
} from "./styles";

interface EditContestModalProps {
  open: boolean;
  onClose: () => void;
  adminCreds: AdminCreds;
}

const formatStartTime = (value?: string): string => {
  if (!value) return "—";
  const parsed = dayjs(value.replace("Z", "+00:00"));
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm UTC") : value;
};

export const EditContestModal = ({
  open,
  onClose,
  adminCreds,
}: EditContestModalProps) => {
  const [contests, setContests] = useState<ActualAssessmentListItem[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [jsonText, setJsonText] = useState("");

  const [contestId, setContestId] = useState("");
  const [title, setTitle] = useState("");
  const [answersTimeMinutes, setAnswersTimeMinutes] = useState(
    String(DEFAULT_ANSWERS_TIME_MINUTES)
  );
  const [activeTimeMinutes, setActiveTimeMinutes] = useState(
    String(DEFAULT_ANSWERS_TIME_MINUTES)
  );
  const [startTime, setStartTime] = useState<Dayjs | null>(
    defaultContestStartTime()
  );
  const [timezoneId, setTimezoneId] =
    useState<ContestTimezoneId>(DEFAULT_TIMEZONE_ID);
  const [presetId, setPresetId] =
    useState<ContestPresetId>(DEFAULT_PRESET_ID);
  const [sendEmail, setSendEmail] = useState<ContestSendEmailChoice>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [listLoading, setListLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedId("");
    setJsonText("");
    setContestId("");
    setTitle("");
    setAnswersTimeMinutes(String(DEFAULT_ANSWERS_TIME_MINUTES));
    setActiveTimeMinutes(String(DEFAULT_ANSWERS_TIME_MINUTES));
    setStartTime(defaultContestStartTime());
    setTimezoneId(DEFAULT_TIMEZONE_ID);
    setPresetId(DEFAULT_PRESET_ID);
    setSendEmail(null);
  };

  const applyHydratedSettings = (
    assessment: Record<string, unknown>,
    nextTimezoneId: ContestTimezoneId = DEFAULT_TIMEZONE_ID
  ) => {
    const hydrated = hydrateContestSettings(assessment, nextTimezoneId);
    setContestId(hydrated.contestId);
    setTitle(hydrated.title);
    setAnswersTimeMinutes(hydrated.answersTimeMinutes);
    setActiveTimeMinutes(hydrated.activeTimeMinutes);
    setStartTime(hydrated.startTime);
    setTimezoneId(hydrated.timezoneId);
    setPresetId(hydrated.presetId);
    setSendEmail(null);
  };

  const syncFiltersIntoJson = (settings: ContestFormSettings) => {
    setJsonText((prev) => {
      const parsed = tryParseContestJson(prev);
      if (!parsed) return prev;

      const updated = applyContestSettingsToJson(parsed, settings);
      if (!updated) return prev;

      return JSON.stringify(updated, null, 2);
    });
  };

  const currentSettings = (
    overrides: Partial<ContestFormSettings> = {}
  ): ContestFormSettings => ({
    contestId,
    title,
    answersTimeMinutes,
    activeTimeMinutes,
    startTime,
    timezoneId,
    presetId,
    sendEmail,
    ...overrides,
  });

  const loadContests = useCallback(async () => {
    setListLoading(true);
    setFormError(null);
    try {
      const list = await getActualAssessmentList(adminCreds);
      setContests(list);
      setSelectedId((prev) => {
        if (!list.some((item) => item.id === prev)) {
          setJsonText("");
          return "";
        }
        return prev;
      });
    } catch (error) {
      const errorMessage = getAssessmentApiErrorMessage(
        error,
        "Oops... Unknown error happened while loading contests"
      );
      console.error("Error loading contests:", errorMessage);
      setFormError(errorMessage);
      setContests([]);
      resetForm();
    } finally {
      setListLoading(false);
    }
  }, [adminCreds.adminLogin, adminCreds.adminPassword]);

  useEffect(() => {
    if (!open) return;
    void loadContests();
  }, [open, loadContests]);

  const handleClose = () => {
    setContests([]);
    resetForm();
    setFormError(null);
    setSuccessMessage(null);
    setListLoading(false);
    setSaveLoading(false);
    onClose();
  };

  const handleSelectContest = (item: ActualAssessmentListItem) => {
    setSelectedId(item.id);
    applyHydratedSettings(item.raw);
    setJsonText(JSON.stringify(item.raw, null, 2));
    setFormError(null);
    setSuccessMessage(null);
  };

  const handleTimezoneChange = (nextTimezoneId: ContestTimezoneId) => {
    const nextStartTime =
      startTime && startTime.isValid()
        ? convertWallTimeBetweenZones(startTime, timezoneId, nextTimezoneId)
        : startTime;
    setTimezoneId(nextTimezoneId);
    setStartTime(nextStartTime);
    setFormError(null);
    setSuccessMessage(null);
    syncFiltersIntoJson(
      currentSettings({
        timezoneId: nextTimezoneId,
        startTime: nextStartTime,
      })
    );
  };

  const handleJsonChange = (value: string) => {
    setJsonText(value);
    setFormError(null);
    setSuccessMessage(null);
  };

  const handleJsonBlur = () => {
    const parsed = tryParseContestJson(jsonText);
    if (!parsed) return;
    applyHydratedSettings(parsed, timezoneId);
    setJsonText(JSON.stringify(parsed, null, 2));
  };

  const handleSave = async () => {
    if (!selectedId) {
      setFormError("Select a contest to edit");
      return;
    }

    const trimmed = jsonText.trim();
    if (!trimmed) {
      setFormError("Contest JSON is empty");
      return;
    }

    const parsed = tryParseContestJson(trimmed);
    if (!parsed) {
      setFormError("Invalid JSON. Please fix the syntax and try again");
      return;
    }

    if (sendEmail === null) {
      setFormError("Choose whether to send invitation emails");
      return;
    }

    const payload = applyContestSettingsToJson(
      parsed,
      currentSettings({ contestId: selectedId })
    );
    if (!payload) {
      if (
        !Number.isInteger(Number(answersTimeMinutes)) ||
        Number(answersTimeMinutes) <= 0
      ) {
        setFormError("Answers time must be a positive number of minutes");
        return;
      }
      if (
        !Number.isInteger(Number(activeTimeMinutes)) ||
        Number(activeTimeMinutes) <= 0
      ) {
        setFormError("Active time must be a positive number of minutes");
        return;
      }
      setFormError("Choose a valid contest start time");
      return;
    }

    setJsonText(JSON.stringify(payload, null, 2));
    setSaveLoading(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      await deleteAssessment(selectedId, adminCreds);
      try {
        const res = await addAssessment(payload, adminCreds);
        const message =
          typeof res === "string" && res.trim()
            ? res
            : "Contest updated successfully";
        setSuccessMessage(message);
        resetForm();
        await loadContests();
      } catch (addError) {
        const addMessage = getAssessmentApiErrorMessage(
          addError,
          "Failed to re-upload contest after delete"
        );
        setFormError(
          `Contest was deleted, but re-upload failed: ${addMessage}. Re-upload manually with the same id.`
        );
      }
    } catch (error) {
      const errorMessage = getAssessmentApiErrorMessage(
        error,
        "Oops... Unknown error happened while updating contest"
      );
      console.error("Error updating contest:", errorMessage);
      setFormError(errorMessage);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyles}>
        <Box sx={fieldContainerStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={headerTypographyStyles}
          >
            Edit contest
          </CustomTypography>

          <Typography variant="body2" sx={hintTypographyStyles}>
            Select a contest, change settings (including preset), then save.
            Changes are applied by deleting and re-uploading with the same id.
          </Typography>

          {formError && (
            <Typography variant="body2" sx={errorTypographyStyles}>
              {formError}
            </Typography>
          )}

          {successMessage && (
            <Typography variant="body2" sx={successTypographyStyles}>
              {successMessage}
            </Typography>
          )}
        </Box>

        {listLoading ? (
          <Box sx={listLoadingStyles}>
            <CircularProgress size={32} />
          </Box>
        ) : contests.length === 0 ? (
          <Typography variant="body2" sx={emptyListTypographyStyles}>
            No active contests found
          </Typography>
        ) : (
          <Box sx={listContainerStyles}>
            {contests.map((contest) => (
              <Box
                key={contest.id}
                sx={contestItemStyles(selectedId === contest.id)}
                onClick={() => handleSelectContest(contest)}
              >
                <Typography
                  variant="subtitle2"
                  sx={contestTitleTypographyStyles}
                >
                  {contest.title}
                </Typography>
                <Typography variant="body2" sx={contestMetaTypographyStyles}>
                  id: {contest.id}
                  {contest.focus ? ` · ${contest.focus}` : ""}
                  {" · "}
                  {formatStartTime(contest.time_to_send)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {selectedId && (
          <form className="flex flex-col gap-[16px]">
            <Typography variant="body2" sx={selectedHintStyles}>
              Editing id: {selectedId} (kept the same on save)
            </Typography>

            <Box sx={fieldsGridStyles}>
              <TextField
                fullWidth
                label="Contest id"
                value={contestId}
                disabled
                sx={getTextFieldStyles(false)}
              />

              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setTitle(nextValue);
                  setFormError(null);
                  setSuccessMessage(null);
                  syncFiltersIntoJson(currentSettings({ title: nextValue }));
                }}
                sx={getTextFieldStyles(false)}
              />

              <TextField
                fullWidth
                type="number"
                label="Answers time (minutes)"
                value={answersTimeMinutes}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setAnswersTimeMinutes(nextValue);
                  setFormError(null);
                  setSuccessMessage(null);
                  syncFiltersIntoJson(
                    currentSettings({ answersTimeMinutes: nextValue })
                  );
                }}
                inputProps={{ min: 1, step: 1 }}
                sx={getTextFieldStyles(false)}
              />

              <TextField
                fullWidth
                type="number"
                label="Active time (minutes)"
                value={activeTimeMinutes}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setActiveTimeMinutes(nextValue);
                  setFormError(null);
                  setSuccessMessage(null);
                  syncFiltersIntoJson(
                    currentSettings({ activeTimeMinutes: nextValue })
                  );
                }}
                inputProps={{ min: 1, step: 1 }}
                sx={getTextFieldStyles(false)}
              />

              <FormControl fullWidth>
                <InputLabel sx={getInputLabelStyles(false)}>
                  Start time timezone
                </InputLabel>
                <Select
                  label="Start time timezone"
                  value={timezoneId}
                  onChange={(event) => {
                    handleTimezoneChange(
                      event.target.value as ContestTimezoneId
                    );
                  }}
                  sx={getSelectStyles(false)}
                  MenuProps={{
                    PaperProps: {
                      sx: presetMenuPaperStyles,
                    },
                  }}
                >
                  {CONTEST_TIMEZONES.map((zone) => (
                    <MenuItem
                      key={zone.id}
                      value={zone.id}
                      sx={menuItemStyles}
                    >
                      {zone.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Contest start time"
                  value={startTime}
                  onChange={(newValue) => {
                    setStartTime(newValue);
                    setFormError(null);
                    setSuccessMessage(null);
                    syncFiltersIntoJson(
                      currentSettings({ startTime: newValue })
                    );
                  }}
                  format="DD.MM.YYYY HH:mm"
                  ampm={false}
                  timeSteps={{ minutes: 5 }}
                  open={isDatePickerOpen}
                  onOpen={() => {
                    setIsDatePickerOpen(true);
                    setFormError(null);
                  }}
                  onClose={() => setIsDatePickerOpen(false)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onClick: () => setIsDatePickerOpen(true),
                      sx: getTextFieldStyles(false),
                    },
                    popper: {
                      sx: popperStyles,
                    },
                    layout: {
                      sx: layoutStyles,
                    },
                    day: {
                      sx: dayStyles,
                    },
                    yearButton: {
                      sx: yearButtonStyles,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <FormControl fullWidth>
              <InputLabel sx={getInputLabelStyles(false)}>Preset</InputLabel>
              <Select
                label="Preset"
                value={presetId}
                onChange={(event) => {
                  const nextPresetId = event.target.value as ContestPresetId;
                  setPresetId(nextPresetId);
                  setFormError(null);
                  setSuccessMessage(null);
                  syncFiltersIntoJson(
                    currentSettings({ presetId: nextPresetId })
                  );
                }}
                sx={getSelectStyles(false)}
                MenuProps={{
                  PaperProps: {
                    sx: presetMenuPaperStyles,
                  },
                }}
              >
                {CONTEST_PRESETS.map((preset) => (
                  <MenuItem
                    key={preset.id}
                    value={preset.id}
                    sx={menuItemStyles}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel sx={getInputLabelStyles(false)}>
                Send invitation emails?
              </InputLabel>
              <Select
                label="Send invitation emails?"
                value={sendEmail === null ? "" : sendEmail ? "yes" : "no"}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextSendEmail: ContestSendEmailChoice =
                    nextValue === "yes"
                      ? true
                      : nextValue === "no"
                        ? false
                        : null;
                  setSendEmail(nextSendEmail);
                  setFormError(null);
                  setSuccessMessage(null);
                  if (nextSendEmail !== null) {
                    syncFiltersIntoJson(
                      currentSettings({ sendEmail: nextSendEmail })
                    );
                  }
                }}
                sx={getSelectStyles(false)}
                MenuProps={{
                  PaperProps: {
                    sx: presetMenuPaperStyles,
                  },
                }}
              >
                <MenuItem value="yes" sx={menuItemStyles}>
                  Yes
                </MenuItem>
                <MenuItem value="no" sx={menuItemStyles}>
                  No
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              multiline
              minRows={12}
              maxRows={24}
              fullWidth
              value={jsonText}
              onChange={(event) => handleJsonChange(event.target.value)}
              onBlur={handleJsonBlur}
              placeholder="Contest JSON"
              sx={jsonTextFieldStyles}
            />
          </form>
        )}

        <Box sx={buttonsContainerStyles}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={secondaryButtonStyles}
            disabled={saveLoading}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={mainButtonStyles}
            disabled={saveLoading || !selectedId || listLoading}
          >
            {saveLoading ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
